<template>
	<div class="tw-pb-10">
		<div class="tw-flex tw--mx-2">
			<div
				class="tw-px-2"
				:class="{
					'tw-w-4/6': selectedJobPosition.id,
					'tw-w-full': !selectedJobPosition.id,
				}"
			>
				<p-card class="tw-mb-6">
					<card-header>
						<job-hierarchy-header />
					</card-header>
					<div class="tw-py-6" style="min-height: 300px;">
						<job-hierarchy-form
							:hierarchy-assignation="displayedAssignation"
							:selected-job-position="selectedJobPosition"
							:selected-job-position-level="selectedJobPositionLevel"
							:filtered-area-id="filteredAreaId"
							@selectedJobPosition="onSelectedJobPosition"
					@close="onCloseClicked"
						/>
					</div>
				</p-card>
			</div>
			<div class="tw-w-2/6 tw-px-2" v-if="selectedJobPosition.id">
				<hierarchy-children-list-card
					:selected-job-position="selectedJobPosition"
					:hierarchy-assignation="hierarchyAssignation"
					:filtered-area-id="filteredAreaId"
					@closeClick="onCloseClicked"
				/>
			</div>
		</div>
		<div class="tw-text-center">
			<v-btn class="custom-button black" @click="goBackNavigation">Atrás</v-btn>
		<v-btn
			color="primary"
			class="custom-button tw-px-8"
			@click="generateOrganizationChart"
		>
				Generar Organigrama
			</v-btn>
		</div>
		<div class="tw-text-center tw-mt-4" v-if="!allHaveParent">
			<div class="unassigned-warning">
				<v-icon size="20" class="warning-icon">warning</v-icon>
				<div class="warning-content">
					<p class="tw-m-0">
						Hay puestos sin jefe asignado. Estos puestos no aparecerán en el
						organigrama.
					</p>
				</div>
			</div>
		</div>
		<v-dialog v-model="showParentlessDialog" persistent max-width="450">
			<p-card class="tw-p-6">
				<v-card-title class="dialog-title">
					Puestos sin jefe asignado
				</v-card-title>
				<v-card-text>
					<p class="dialog-message">
						Los siguientes puestos no aparecerán en el organigrama:
					</p>
					<ul class="parentless-list">
						<li v-for="job in parentlessJobs" :key="job.id">
							{{ job.name }}
						</li>
					</ul>
					<p class="dialog-message tw-mt-3">
						¿Deseas generar el organigrama de todas formas?
					</p>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn
						color="primary"
						class="custom-button black"
						style="margin-right: 10px !important;"
						@click="showParentlessDialog = false"
						>Cancelar</v-btn
					>
					<v-btn
						color="primary"
						class="custom-button"
						@click="confirmGenerateOrganizationChart"
						>Generar organigrama</v-btn
					>
				</v-card-actions>
			</p-card>
		</v-dialog>
	</div>
</template>

<script>
import PCard from '@/modules/shared/components/ui/Card/p-card';
import CardHeader from '@/modules/shared/components/ui/Card/CardHeader';
import JobHierarchyHeader from '@/modules/job-evaluation/components/supplies/guideline-model/hierarchy/hierarchy-assignation/job-hierarchy-header';
import JobHierarchyForm from '@/modules/job-evaluation/components/supplies/guideline-model/hierarchy/job-hierarchy-form';
import { mapState } from 'vuex';
import { pathOr, pipe, pluck, drop, all, isNil, prop, filter as rFilter } from 'ramda';
import HierarchyChildrenListCard from '@/modules/job-evaluation/components/supplies/guideline-model/hierarchy/hierarchy-children/hierarchy-children-list-card';
import bus from '@/bus/app-bus';

const LOADER_EVENT = 'app-event-loader-toggle-view';
const LOADER_MESSAGE = 'Praktiko esta cargando la jerarquía.';

export default {
	name: 'page-supply-guideline-model-hierarchy',
	components: {
		HierarchyChildrenListCard,
		JobHierarchyForm,
		JobHierarchyHeader,
		CardHeader,
		PCard,
	},
	beforeMount() {
		bus.$emit(LOADER_EVENT, { show: true, message: LOADER_MESSAGE });
		Promise.all([
			this.$store.dispatch('jobEvaluation/loadGroups'),
			this.$store.dispatch('jobEvaluation/getGuidelineModelStatus'),
		]).finally(() => {
			bus.$emit(LOADER_EVENT, { show: false });
		});
	},
	beforeDestroy() {
		bus.$emit(LOADER_EVENT, { show: false });
	},
	data() {
		return {
			selectedJobPosition: {},
			selectedJobPositionLevel: -1,
			showParentlessDialog: false,
		};
	},
	computed: {
		filteredAreaId() {
			const areaId = this.$route.query.areaId;
			return areaId ? Number(areaId) : null;
		},
		...mapState('jobEvaluation', {
			hierarchyAssignation: pathOr(
				[],
				['supplies', 'guidelineModel', 'hierarchyAssignation'],
			),
		}),
		displayedAssignation() {
			if (!this.filteredAreaId) return this.hierarchyAssignation;
			const areaId = this.filteredAreaId;
			const isModifying = !!this.selectedJobPosition.id;
			const selectedLevel = this.selectedJobPositionLevel;
			// Collect IDs of jobs that belong to the filtered area
			const areaJobIds = new Set();
			this.hierarchyAssignation.forEach((level) => {
				level.jobPositions.forEach((job) => {
					if (job.areaId === areaId) areaJobIds.add(job.id);
				});
			});
			return rFilter(
				a => a.jobPositions.length > 0,
				this.hierarchyAssignation.map((assignation, originalLevel) => {
					// While modifying: levels at/above the selected boss's level show
					// ALL jobs (cross-area candidates surface). Upper levels stay filtered.
					const showAllJobs = isModifying && originalLevel >= selectedLevel;
					return {
						...assignation,
						originalLevel,
						jobPositions: showAllJobs
							? assignation.jobPositions
							: rFilter(
								job => job.areaId === areaId ||
									(job.fatherId && areaJobIds.has(job.fatherId)),
								assignation.jobPositions,
							),
					};
				}),
			);
		},
		allHaveParent() {
			return pipe(
				pluck('jobPositions'),
				drop(1),
				all((jobPositions) => {
					const configured = jobPositions.filter(j => j.typeVerb && !j.isHighestAuthority);
					return configured.length === 0 ||
						all(j => !isNil(prop('fatherId', j)), configured);
				}),
			)(this.hierarchyAssignation);
		},
		parentlessJobs() {
			const jobs = [];
			this.hierarchyAssignation.forEach((level, index) => {
				if (index >= 1) {
					(level.jobPositions || []).forEach((jp) => {
						if (jp.typeVerb && !jp.fatherId && !jp.isHighestAuthority) jobs.push(jp);
					});
				}
			});
			return jobs;
		},
	},
	methods: {
		goBackNavigation() {
			this.goTo('job-supply-guideline-model');
		},
		onSelectedJobPosition({ jobPosition, level }) {
			this.selectedJobPosition = jobPosition;
			this.selectedJobPositionLevel = level;
		},
		onCloseClicked() {
			this.selectedJobPosition = {};
			this.selectedJobPositionLevel = -1;
		},
		generateOrganizationChart() {
			if (this.parentlessJobs.length > 0) {
				this.showParentlessDialog = true;
				return;
			}
			this.confirmGenerateOrganizationChart();
		},
		confirmGenerateOrganizationChart() {
			this.showParentlessDialog = false;
			this.$store
				.dispatch('jobEvaluation/generateOrganizationChart')
				.then(() => {
					const options = {};
					if (this.filteredAreaId) {
						options.query = { areaId: this.filteredAreaId };
					}
					this.goTo('job-supply-organization-chart', options);
				})
				.catch(e => console.error(e));
		},
	},
};
</script>

<style scoped>
.unassigned-warning {
	display: inline-flex;
	align-items: flex-start;
	gap: 12px;
	border: 1px solid #f09c2b;
	background-color: rgba(240, 156, 43, 0.09);
	padding: 12px 20px;
	border-radius: 20px;
	max-width: 650px;
	text-align: left;
}
.unassigned-warning .warning-icon {
	color: #f09c2b;
	flex-shrink: 0;
	margin-top: 2px;
}
.unassigned-warning .warning-content p {
	font-size: 13px;
	line-height: 1.5;
	color: var(--app-color-secondary);
}
.dialog-title {
	font-size: 20px;
	line-height: 24px;
	color: var(--app-color-info);
}
.dialog-message {
	color: var(--app-color-info);
	font-size: 14px;
}
.parentless-list {
	margin: 8px 0;
	padding-left: 20px;
}
.parentless-list li {
	font-size: 14px;
	line-height: 1.8;
	color: var(--app-color-secondary);
	font-weight: 500;
}
</style>



// WEBPACK FOOTER //
// src/modules/job-evaluation/pages/supplies/guideline-model/page-supply-guideline-model-hierarchy.vue